import { useForm } from '@tanstack/react-form';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Image01, Upload01 } from '@untitled-ui/icons-react';

import { Button } from '../../../../../components/buttons/button';
import { DropZone } from '../../../../../components/drop-zone/drop-zone';
import { TextArea } from '../../../../../components/inputs/text-area';
import { TextField } from '../../../../../components/inputs/text-field';
import { SwitchField } from '../../../../../components/switch';
import { getConfig } from '../../../../../config';
import { trackError } from '../../../../../error/tracking';
import { captureException } from '../../../../../logger/capture-exception';
import { getQueryClient } from '../../../../../sdk/request';
import { getAuthUserFromCache } from '../../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../../services/translation';
import { FooterProductFunnel } from './-components/footer-product-funnel';

const t = tNamespace('app.funnel.product.$id._layout-funnel.product-info');

export const Route = createFileRoute('/app/funnel/product/$id/_layout-funnel/product-info')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  // TanStack Query hook for auth user
  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);
  const form = useForm({
    defaultValues: {
      brandName: '',
      uniqueID: '',
      productCategory: '',
      productImages: null as File | null,
      productExtraDocuments: null as File | null,
      extraInformation: '',
      useInstructionsOnly: false,
    },
    onSubmit: function () {
      // TODO: To remove, for testing purposes
      if (currentUser == null) {
        // TODO: capture
        return;
      }
      // TODO: What are we doing here ? Upload the image first wait for answer to add the image url to the product we upload and and update without the image url
    },
  });

  // TODO: Add validators for the fields when entity is perfectly defined
  return (
    <>
      <form className="flex w-full max-w-content flex-col gap-6">
        <div className="flex flex-col gap-6 rounded-md border border-gray-200 p-4">
          <h2 className="font-display-2">{t('title')}</h2>
          <div className="flex gap-4">
            <form.Field
              name="brandName"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <TextField
                    label={t('brandName')}
                    placeholder={t('brandName')}
                    sizeField="lg"
                    value={field.state.value}
                    onChange={function (e) {
                      field.handleChange(e.target.value);
                    }}
                  />
                );
              }}
            />
            <form.Field
              name="uniqueID"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <TextField
                    label={t('uniqueID')}
                    placeholder={t('uniqueID')}
                    sizeField="lg"
                    value={field.state.value}
                    onChange={function (e) {
                      field.handleChange(e.target.value);
                    }}
                  />
                );
              }}
            />
          </div>
          <form.Field
            name="productCategory"
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextField
                  label={t('productType')}
                  placeholder={t('productTypePlaceholder')}
                  sizeField="lg"
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                />
              );
            }}
          />
        </div>
        <div className="flex flex-col gap-6 rounded-md border border-gray-200 p-4">
          <div className="flex flex-col gap-2">
            <h2 className="font-display-2">{t('aiGuidances')}</h2>
            <p className="text-gray-500 text-sm">{t('aiGuidancesDesc')}</p>
          </div>
          <div className="flex gap-4">
            <form.Field
              name="productImages"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <DropZone
                    onFileSelect={function (file: File) {
                      field.handleChange(file);
                    }}
                    label={t('productImages')}
                    maxSize={getConfig().imagesMaxSize}
                    maxFiles={1}
                    icon={<Image01 className="text-gray-500 h-6 w-6" />}
                    acceptedFileTypes={getConfig().imagesFileFormat}
                  />
                );
              }}
            />
            <form.Field
              name="productExtraDocuments"
              // eslint-disable-next-line react/no-children-prop
              children={function (field) {
                return (
                  <DropZone
                    onFileSelect={function (file: File) {
                      field.handleChange(file);
                    }}
                    label={t('productExtraDocuments')}
                    maxSize={getConfig().extraDocumentsMaxSize}
                    maxFiles={1}
                    icon={<Upload01 className="text-gray-500 h-6 w-6" />}
                    acceptedFileTypes={getConfig().extraDocumentsFileFormat}
                  />
                );
              }}
            />
          </div>
          <form.Field
            name="extraInformation"
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <TextArea
                  label={t('additionalInformation')}
                  counterLimit={1500}
                  showCounter={true}
                  placeholder={t('additionalInformationPlaceholder')}
                  value={field.state.value}
                  onChange={function (e) {
                    field.handleChange(e.target.value);
                  }}
                  size="md"
                  rows={5}
                  error={field.state.meta?.errors}
                />
              );
            }}
          />
          <p className="text-gray-500 font-body-1-sb">{t('extraContentOption')}</p>
          <form.Field
            name="useInstructionsOnly"
            // eslint-disable-next-line react/no-children-prop
            children={function (field) {
              return (
                <div className="flex flex-row items-center gap-2">
                  <SwitchField
                    size="md"
                    label={t('useInstructionsOnly')}
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                  />
                </div>
              );
            }}
          />
        </div>
        <FooterProductFunnel
          cancel={<Button variant="outline">{tGlobal('cancel')}</Button>}
          next={
            <Button
              variant="accent"
              onClick={function () {
                form.handleSubmit().catch(function (_err) {
                  captureException(
                    _err,
                    {
                      file: 'routes/app/funnel/product/$id/_layout-funnel.product-info.tsx',
                      method: 'onClick',
                      infoMessage: 'unable submit form',
                    },
                    trackError
                  );
                });
              }}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              {tGlobal('generate')}
            </Button>
          }
        />
      </form>
    </>
  );
}
